import React, {useState} from 'react';
import {Modal, Upload, Button, message, Table} from 'antd';
import {UploadOutlined} from '@ant-design/icons';
import {
    AccountDeliveryDriverImportResponse,
    batchCreateAccountDeliveryDriverForDsp,
    batchCreateAccountDeliveryDriverForCentral,
    batchCreateAccountDeliveryDriverForWms,
} from '../graphql/mutations/batchAccountDeliveryDriverExcel';
import {fileUpload} from '../../../../utils/storageUtils';
import {t} from '../../../../i18n';
import {getLocalizedErrorMessage} from '../types/AccountDeliveryDriverErrorMessage';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import BatchUploadDriverPhotosModal from './BatchUploadDriverPhotosModal';
import { AccountsPageMode, ACCOUNTS_PAGE_MODE } from '../hooks/useAccountDeliveryDriverQuery';

const {Dragger} = Upload;

interface AccountDeliveryDriverBatchCreateProps {
    visible: boolean;
    onCancel: () => void;
    onSuccess: () => void;
    mode?: AccountsPageMode;
}

const AccountDeliveryDriverBatchCreateModal: React.FC<AccountDeliveryDriverBatchCreateProps> = ({
                                                                                                    visible,
                                                                                                    onCancel,
                                                                                                    onSuccess,
                                                                                                    mode,
                                                                                                }) => {
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState<AccountDeliveryDriverImportResponse | null>(null);
    const [photoUploadModalVisible, setPhotoUploadModalVisible] = useState(false);
    const [photoUploadAccountIds, setPhotoUploadAccountIds] = useState<number[]>([]);
    const { currentWarehouse } = useWarehouseContext();

    const isWms = mode === ACCOUNTS_PAGE_MODE.WMS;
    const isDsp = mode === ACCOUNTS_PAGE_MODE.DSP;
    const isCentral = mode === ACCOUNTS_PAGE_MODE.CENTRAL;

    const handleUpload = async (file: File) => {
        // mode 必须是三种合法入口之一，否则不调用任何导入接口
        if (!isWms && !isDsp && !isCentral) {
            message.error(t('delivery.personnel.message.uploadFailed'));
            return;
        }

        setUploading(true);
        try {
            // 1. 先上传文件到对象存储
            const uploadResponse = await fileUpload(file);

            if (!uploadResponse.objectKey) {
                throw new Error(t('delivery.personnel.message.fileUploadFailed'));
            }

            // 2. 根据入口调用对应的批量导入接口
            let result: AccountDeliveryDriverImportResponse;
            if (isWms) {
                result = await batchCreateAccountDeliveryDriverForWms(
                    uploadResponse.objectKey,
                    parseInt(currentWarehouse!.id)
                );
            } else if (isDsp) {
                result = await batchCreateAccountDeliveryDriverForDsp(uploadResponse.objectKey);
            } else {
                result = await batchCreateAccountDeliveryDriverForCentral(uploadResponse.objectKey);
            }

            setUploadResult(result);

            if (result && result.successCount > 0) {
                message.success(t('delivery.personnel.message.successfullyCreatedCount', {count: result.successCount}));
            }

            if (result && result.failedCount > 0) {
                message.warning(t('delivery.personnel.message.failedCount', {count: result.failedCount}));
            }
        } catch (error: any) {
            const errorMessage = error?.message || t('delivery.personnel.message.uploadFailed');
            message.error(errorMessage);
            console.error('Upload error:', error);
        } finally {
            setUploading(false);
        }
    };

    const uploadProps = {
        accept: '.xlsx,.xls',
        showUploadList: false,
        beforeUpload: (file: File) => {
            // 验证文件类型
            const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                file.type === 'application/vnd.ms-excel' ||
                file.name.toLowerCase().endsWith('.xlsx') ||
                file.name.toLowerCase().endsWith('.xls');

            if (!isExcel) {
                message.error(t('delivery.personnel.message.onlyExcelAllowed'));
                return false;
            }

            // 验证文件大小（限制为 10MB）
            const isLt10M = file.size / 1024 / 1024 < 10;
            if (!isLt10M) {
                message.error(t('delivery.personnel.message.fileSizeLimit'));
                return false;
            }

            handleUpload(file);
            return false;
        },
    };

    const failureColumns = [
        {
            title: t('delivery.personnel.label.phoneNumber'),
            dataIndex: 'phone',
            key: 'phone',
            width: 150,
        },
        {
            title: t('delivery.personnel.label.failureReason'),
            dataIndex: 'errorMessage',
            key: 'errorMessage',
            render: (_: any, record: any) => {
                const msg = getLocalizedErrorMessage(record);
                return <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{msg}</div>;
            },
        },
    ];

    const handleModalClose = () => {
        // 保存成功账号ID（在清空之前）
        const accountIds = uploadResult?.successAccountIds;
        setUploadResult(null);
        
        // 如果有成功创建的账号，弹出照片上传弹窗
        if (accountIds && accountIds.length > 0) {
            // 延迟一下，确保当前弹窗完全关闭
            setTimeout(() => {
                setPhotoUploadModalVisible(true);
                setPhotoUploadAccountIds(accountIds);
            }, 300);
        }
        
        onCancel();
    };

    const handlePhotoUploadSuccess = () => {
        setPhotoUploadModalVisible(false);
        setPhotoUploadAccountIds([]);
        onSuccess();
    };

    const handlePhotoUploadCancel = () => {
        setPhotoUploadModalVisible(false);
        setPhotoUploadAccountIds([]);
    };

    return (
        <>
        <Modal
            title={
                <div className="flex justify-between items-center">
                    <div className="flex items-center">
                        <div className="h-6 w-1 bg-purple-600 mr-3"></div>
                        <span
                            className="text-xl font-medium">{t('delivery.personnel.label.batchCreateDeliveryDriver')}</span>
                    </div>
                </div>
            }
            open={visible}
            onCancel={handleModalClose}
            footer={[
                <Button key="cancel" onClick={handleModalClose}>
                    {uploadResult ? t('delivery.personnel.label.close') : t('delivery.personnel.label.cancel')}
                </Button>
            ]}
            width={600}
        >
            {!uploadResult ? (
                <>
                    <Dragger {...uploadProps} disabled={uploading}>
                        <p className="ant-upload-drag-icon">
                            <UploadOutlined/>
                        </p>
                        <p className="ant-upload-text">
                            {uploading ? t('delivery.personnel.label.uploading') : t('delivery.personnel.label.dragOrClickToUpload')}
                        </p>
                    </Dragger>
                </>
            ) : (
                <>
                    <div className="flex flex-col h-full">
                        <div className="flex-1 space-y-4 overflow-y-auto">
                            <div className="grid grid-cols-2 gap-y-2 max-w-xs">
                                <div
                                    className="text-gray-500 text-left">{t('delivery.personnel.label.successfullyCreated')}: {uploadResult.successCount}</div>
                                <div className="text-gray-500"></div>
                                <div
                                    className="text-gray-500 text-left">{t('delivery.personnel.label.creationFailed')}: {uploadResult.failedCount}</div>
                            </div>
                        </div>
                    </div>

                    <Table
                        columns={failureColumns}
                        dataSource={uploadResult.failedRecords}
                        rowKey={(record, index) => `${record.phone}-${index}`}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: false,
                            showQuickJumper: true,
                            showTotal: (total) => t('table.pagination.totalItems', { total }),
                        }}
                        size="small"
                        scroll={{y: 300}}
                        bordered
                    />
                </>
            )}
        </Modal>
        <BatchUploadDriverPhotosModal
            visible={photoUploadModalVisible}
            accountIds={photoUploadAccountIds}
            onSuccess={handlePhotoUploadSuccess}
            onCancel={handlePhotoUploadCancel}
        />
    </>
    );
};

export default AccountDeliveryDriverBatchCreateModal;